import type { NavBarConfig, NavBarItem } from '@gm-commercial/navbar-model';

import HomeIcon from '~/assets/icons/homeIcon.svg?react';
import PollOutlinedIcon from '~/assets/icons/new/analytics.svg?react';
import ExpensesIcon from '~/assets/icons/new/expenses.svg?react';
import LightbulbIcon from '~/assets/icons/new/lightbulb-02.svg?react';
import MarkerPinIcon from '~/assets/icons/new/markerPinIcon.svg?react';
import ShoppingBagIcon from '~/assets/icons/new/shoppingBag.svg?react';
import ZevoIcon from '~/assets/icons/new/zevo-600.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import {
  CATALOG_AND_PLANS_ACCESS_ROLES,
  SessionConfigType,
} from '~/common/constants/common.constant';
import { AssetReportType } from '~/common/models/asset.model';
import {
  CUSTOM_VIDEO_REQUEST_FLAG,
  FLEETIO_ACCOUNT_LINKING_FLAG,
  FUEL_CARD_AND_EXPENSES_FLAG,
  INSIGHTS_IN_CSP_FLAG,
  MIGRATION_WELCOME_EXPERIENCE_FLAG,
  SAFETY_PAGE_FLAG,
  UNASSIGNED_VEHICLES_SCORE_FLAG,
} from '~/common/models/featureFlags.model';
import type { FleetNavBarConfigParams } from '~/common/models/fleetNavigation.model';
import { FLEET_VEHICLE_ORDERS_LIST } from '~/common/models/pages/fleetPages.model';
import { RouteName } from '~/common/models/route.model';
import { isUnsafeObjectKey } from '~/common/utils/common.utils';
import {
  findRoute,
  makeRoutePath,
  makeRoutePathLite,
} from '~/common/utils/route/route.utils';
import {
  ASSET_REPORT_FILTER_EMPTY_STATE,
  setAssetSessionConfig,
} from '~/features/assets/assetsSlice';
import { DEFAULT_SORT_ORDER } from '~/features/assets/list/utils/vehicleListSort.utils';
import { REPORTS_ACCESS_ROLES } from '~/routes/organizations/reports/constants';
import {
  setCenterOnFleet,
  setSelectedVehicle,
} from '~/store/fleetMap/fleetMapSlice';

export const getFleetNavBarConfig = ({
  profileParams,
  isFleetMapFlagEnabled,
  findFlagScope,
  dispatch,
  sessionId,
  hasRole,
  canShowFleetCatalogByEnablement = true,
  showTelematicsFleetNavSurfaces = true,
  hasEmerNotificationsAccess = false,
  isProtectOnly = false,
  showGettingStarted = false,
  isHardwareEnabledInMarketplace = false,
  showCameraNavSurfaces = true,
}: FleetNavBarConfigParams): NavBarConfig => {
  const context = {
    fleetsId: profileParams?.fleetsId || undefined,
    hubsId: profileParams?.hubsId || undefined,
    organizationsId: profileParams?.organizationsId || undefined,
  };

  const navConfig = {
    fleetLabel: translateText('common:fleet'),
    vehicleOrdersLabel: translateText('order:title'),
    vehicleList: {
      icon: <MarkerPinIcon className="noFill" />,
      listLabel: translateText('common:vehicles'),
    },
  };

  const fleetLabel = navConfig.fleetLabel;
  const vehicleIcon = navConfig.vehicleList.icon;
  // Vehicle map flag controls the default landing page:
  // when enabled, users land on vehicle map.
  // when disabled, users land on vehicle list.
  const vehicleMapPath = makeRoutePathLite({
    routeName: RouteName.ORGANIZATIONS_VEHICLES_MAP,
    params: context,
  });
  const vehiclesPath = makeRoutePathLite({
    routeName: RouteName.ORGANIZATIONS_VEHICLES,
    params: context,
  });
  const vehiclePath =
    isFleetMapFlagEnabled && showTelematicsFleetNavSurfaces
      ? vehicleMapPath
      : vehiclesPath;
  const vehicleListLabel = navConfig.vehicleList.listLabel;
  const vehicleOrdersLabel = navConfig.vehicleOrdersLabel;

  const vehicleNavChildren: NavBarItem[] = [
    ...(showTelematicsFleetNavSurfaces
      ? [
          {
            label: translateText('asset:vehicleMap'),
            testId: 'fleet-vehicle-map',
            path: makeRoutePathLite({
              routeName: RouteName.ORGANIZATIONS_VEHICLES_MAP,
              params: context,
              findFlagScope,
            }),
            callback: () => {
              // Trigger center on fleet when navigating to the vehicle map
              if (dispatch) {
                dispatch(setCenterOnFleet(true));
                dispatch(setSelectedVehicle(null));
                // View mode is now determined by the route - no Redux state needed
              }
            },
          },
        ]
      : []),
    {
      label: vehicleListLabel,
      testId: 'fleet-vehicle-list',
      path: vehiclesPath,
    },
    {
      label: translateText('driver:title'),
      testId: 'fleet-drivers-list',
      path: makeRoutePath(
        findRoute(RouteName.CS_PLATFORM_DRIVERS_LIST),
        context,
        true,
        true,
        findFlagScope
      ),
    },
    {
      label: translateText('peripheral:title'),
      testId: 'fleet-nav-drawer-devices',
      path: makeRoutePath(
        findRoute(RouteName.CS_PLATFORM_PERIPHERALS_LIST),
        context,
        true,
        true,
        findFlagScope
      ),
    },
    {
      label: vehicleOrdersLabel,
      testId: 'fleet-vehicle-orders-list',
      path: makeRoutePath(FLEET_VEHICLE_ORDERS_LIST, context, true, true),
    },
  ];

  // Getting started sits between the fleet switcher and Home when the welcome experience is enabled
  const gettingStartedItem =
    findFlagScope?.(MIGRATION_WELCOME_EXPERIENCE_FLAG) && showGettingStarted
      ? [
          {
            label: translateText('common:gettingStarted'),
            icon: <LightbulbIcon className="noFill" />,
            testId: 'fleet-getting-started',
            path: makeRoutePathLite({
              routeName: RouteName.GETTING_STARTED,
              params: context,
              findFlagScope,
            }),
            bordered: true,
          },
        ]
      : [];

  // Base navigation items
  const baseItems = [
    ...gettingStartedItem,
    {
      label: translateText('common:home'),
      icon: <HomeIcon className="noFill" />,
      testId: 'fleet-home',
      path: makeRoutePathLite({
        routeName: RouteName.ORGANIZATIONS_HOME,
        params: context,
      }),
    },
    {
      label: fleetLabel,
      icon: vehicleIcon,
      testId: 'fleet-vehicles',
      path: vehiclePath,
      children: vehicleNavChildren,
      callback: () => {
        if (dispatch && sessionId && !isUnsafeObjectKey(sessionId)) {
          const safeSessionId = `${sessionId}`;
          dispatch(
            setAssetSessionConfig({
              [SessionConfigType.LIST_VIEW]: {
                [safeSessionId]: {
                  page: 0,
                  continuationToken: null,
                  continuationCache: undefined,
                  activeReport: AssetReportType.FLEET_READINESS,
                  sortOrder: DEFAULT_SORT_ORDER,
                  filterType: ASSET_REPORT_FILTER_EMPTY_STATE,
                  searchCriteria: undefined,
                  selectedAssets: undefined,
                  allSelected: false,
                  includedVehicleIds: undefined,
                  excludedVehicleIds: undefined,
                  selectedIndices: [],
                },
              },
            })
          );
        }
      },
    },
  ];

  const isUnassignedVehiclesScoreEnabled = findFlagScope?.(
    UNASSIGNED_VEHICLES_SCORE_FLAG
  );
  const isCustomVideoRequestEnabled = findFlagScope?.(
    CUSTOM_VIDEO_REQUEST_FLAG
  );

  const safetyItem =
    findFlagScope?.(SAFETY_PAGE_FLAG) &&
    showTelematicsFleetNavSurfaces &&
    !isProtectOnly
      ? [
          {
            label: translateText('common:vehicleEvents'),
            icon: <ZevoIcon className="noFill" />,
            testId: 'fleet-safety',
            path: isUnassignedVehiclesScoreEnabled
              ? makeRoutePathLite({
                  routeName: RouteName.ORGANIZATIONS_SAFETY_LEADERBOARD,
                  params: context,
                  findFlagScope,
                })
              : makeRoutePathLite({
                  routeName: RouteName.ORGANIZATIONS_SAFETY,
                  params: context,
                  findFlagScope,
                }),
            ...(isUnassignedVehiclesScoreEnabled && {
              children: [
                {
                  label: translateText('safety:tabs.driverScores'),
                  testId: 'fleet-safety-driver-scores',
                  path: makeRoutePathLite({
                    routeName: RouteName.ORGANIZATIONS_SAFETY_LEADERBOARD,
                    params: context,
                    findFlagScope,
                  }),
                },
                {
                  label: translateText('safety:tabs.eventHistory'),
                  testId: 'fleet-safety-event-history',
                  path: makeRoutePathLite({
                    routeName: RouteName.ORGANIZATIONS_SAFETY_EVENT_HISTORY,
                    params: context,
                    findFlagScope,
                  }),
                },
                ...(isCustomVideoRequestEnabled && showCameraNavSurfaces
                  ? [
                      {
                        label: translateText('customVideoRequest:title'),
                        testId: 'fleet-safety-custom-video-request',
                        path: makeRoutePathLite({
                          routeName:
                            RouteName.ORGANIZATIONS_CUSTOM_VIDEO_REQUEST,
                          params: context,
                          findFlagScope,
                        }),
                      },
                    ]
                  : []),
              ],
            }),
          },
        ]
      : [];

  // Without TELE, EMER events are all Safety has, and only History shows them.
  const emerOnlySafetyItem =
    findFlagScope?.(SAFETY_PAGE_FLAG) &&
    hasEmerNotificationsAccess &&
    (isProtectOnly || !showTelematicsFleetNavSurfaces)
      ? [
          {
            label: translateText('common:vehicleEvents'),
            icon: <ZevoIcon className="noFill" />,
            testId: 'fleet-safety',
            path: makeRoutePathLite({
              routeName: RouteName.ORGANIZATIONS_SAFETY_EVENT_HISTORY,
              params: context,
              findFlagScope,
            }),
          },
        ]
      : [];

  const reportItem = hasRole?.(REPORTS_ACCESS_ROLES)
    ? [
        {
          label: translateText('common:reports'),
          icon: <PollOutlinedIcon className="noFill" />,
          testId: 'fleet-reports',
          path: makeRoutePathLite({
            routeName: RouteName.ORGANIZATIONS_REPORTS,
            params: context,
            findFlagScope,
          }),
        },
      ]
    : [];

  const shopChildren = [
    ...(canShowFleetCatalogByEnablement
      ? [
          {
            label: translateText('entitlements:planList.title'),
            testId: 'fleet-shop-onstar-plans',
            path: makeRoutePathLite({
              routeName: RouteName.SHOP_PLANS,
              params: context,
              findFlagScope,
            }),
          },
        ]
      : []),
    ...(isHardwareEnabledInMarketplace
      ? [
          {
            label: translateText('catalog:catalogPages.addons.title'),
            testId: 'fleet-shop-addons',
            path: makeRoutePathLite({
              routeName: RouteName.SHOP_ADDONS,
              params: context,
              findFlagScope,
            }),
          },
        ]
      : []),
    ...(findFlagScope?.(FLEETIO_ACCOUNT_LINKING_FLAG) ||
    findFlagScope?.(FUEL_CARD_AND_EXPENSES_FLAG)
      ? [
          {
            label: translateText('common:integrations'),
            testId: 'fleet-shop-integrations',
            path: makeRoutePathLite({
              routeName: RouteName.ORGANIZATIONS_SHOP_INTEGRATIONS,
              params: context,
              findFlagScope,
            }),
          },
        ]
      : []),
  ];

  // only render the Shop parent when at least one child is visible
  const shopItem =
    hasRole?.(CATALOG_AND_PLANS_ACCESS_ROLES) && shopChildren.length > 0
      ? [
          {
            label: translateText('common:shop'),
            icon: <ShoppingBagIcon className="noFill" />,
            testId: 'fleet-shop',
            // First non-empty path: shopChildren[0] could have path='' when
            // canShowFleetCatalogByEnablement is true but CATALOG_FLAG is off,
            // which would cause sanitizeNavDrawerConfig to drop the entire Shop
            // even if a later child (e.g. Add-Ons) has a valid path.
            path: shopChildren.find((c) => c.path)?.path ?? '',
            children: shopChildren,
          },
        ]
      : [];

  const getDataItems = () => {
    const childRoutes: NavBarItem[] = [];

    if (
      findFlagScope?.(INSIGHTS_IN_CSP_FLAG) &&
      showTelematicsFleetNavSurfaces
    ) {
      childRoutes.push({
        label: translateText('insight:title'),
        testId: 'fleet-insights',
        path: makeRoutePathLite({
          routeName: RouteName.ORGANIZATIONS_INSIGHTS,
          params: context,
          findFlagScope,
        }),
      });
    }

    if (hasRole?.(REPORTS_ACCESS_ROLES)) {
      childRoutes.push({
        label: translateText('common:reports'),
        testId: 'fleet-reports',
        path: makeRoutePathLite({
          routeName: RouteName.ORGANIZATIONS_REPORTS,
          params: context,
          findFlagScope,
        }),
      });
    }

    return childRoutes.length > 0
      ? [
          {
            label: translateText('common:data'),
            icon: <PollOutlinedIcon className="noFill" />,
            testId: 'fleet-data',
            children: childRoutes,
          },
        ]
      : [];
  };

  const expensesItem = findFlagScope?.(FUEL_CARD_AND_EXPENSES_FLAG)
    ? [
        {
          label: translateText('common:expenses'),
          icon: <ExpensesIcon className="noFill" />,
          testId: 'fleet-expenses',
          path: makeRoutePathLite({
            routeName: RouteName.ORGANIZATIONS_EXPENSES,
            params: context,
            findFlagScope,
          }),
          children: [
            {
              label: translateText('common:fuelAndCharging'),
              testId: 'fleet-expenses-fuel-and-charging',
              path: makeRoutePathLite({
                routeName: RouteName.ORGANIZATIONS_EXPENSES,
                params: context,
                findFlagScope,
              }),
            },
          ],
        },
      ]
    : [];

  return {
    homePath: '/',
    items: [
      ...baseItems,
      ...safetyItem,
      ...emerOnlySafetyItem,
      ...(findFlagScope?.(INSIGHTS_IN_CSP_FLAG) ? getDataItems() : reportItem), //TODO: Remove this once insights is fully migrated to CSP
      ...shopItem,
      ...expensesItem,
    ],
  };
};
